import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField,
    Avatar, CircularProgress, InputAdornment, Alert,
} from '@mui/material';
import {
    CalendarMonth as CalendarIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Notes as NotesIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    AccessTime as TimeIcon,
    LocationOn as LocationIcon,
} from '@mui/icons-material';
import { format, addMonths, subMonths, startOfMonth } from 'date-fns';

// ── Constants ─────────────────────────────────────────────────────────────────
const DAY_TO_JS   = { SUNDAY:0, MONDAY:1, TUESDAY:2, WEDNESDAY:3, THURSDAY:4, FRIDAY:5, SATURDAY:6 };
const JS_TO_DAY   = Object.fromEntries(Object.entries(DAY_TO_JS).map(([k,v]) => [v,k]));
const WEEK_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h,m] = timeStr.split(':').map(Number);
    return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`;
};

const generateSlots = (startTime, endTime, stepMinutes = 30) => {
    const slots = [];
    const [sh,sm] = startTime.split(':').map(Number);
    const [eh,em] = endTime.split(':').map(Number);
    let total = sh*60 + sm;
    const endTotal = eh*60 + em;
    // Only offer a slot if a full meeting fits before the availability block ends
    while (total + stepMinutes <= endTotal) {
        slots.push(`${String(Math.floor(total/60)).padStart(2,'0')}:${String(total%60).padStart(2,'0')}:00`);
        total += stepMinutes;
    }
    return slots;
};

const toDateStr = (d) => {
    const y=d.getFullYear(); const mo=String(d.getMonth()+1).padStart(2,'0'); const day=String(d.getDate()).padStart(2,'0');
    return `${y}-${mo}-${day}`;
};

// ── Component ─────────────────────────────────────────────────────────────────
const BookTime = () => {
    const { username } = useParams();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';

    const [profile,        setProfile]        = useState(null);
    const [loading,        setLoading]        = useState(true);
    const [notFound,       setNotFound]       = useState(false);
    const [viewDate,       setViewDate]       = useState(startOfMonth(new Date()));
    const [selectedDate,   setSelectedDate]   = useState(null);
    const [selectedTime,   setSelectedTime]   = useState(null);
    const [requesterName,  setRequesterName]  = useState('');
    const [requesterEmail, setRequesterEmail] = useState('');
    const [message,        setMessage]        = useState('');
    const [submitting,     setSubmitting]     = useState(false);
    const [submitted,      setSubmitted]      = useState(false);
    const [error,          setError]          = useState('');

    useEffect(() => {
        fetch(`${API_URL}/public/schedule/${username}`)
            .then(res => { if (!res.ok) throw new Error(); return res.json(); })
            .then(data => { setProfile(data); setLoading(false); })
            .catch(() => { setNotFound(true); setLoading(false); });
    }, [username, API_URL]);

    const schedule = profile?.schedule || [];
    const recurringByDOW = {};
    const specificByDate = {};
    schedule.forEach(s => {
        if (s.repeatWeekly !== false) {
            const js = DAY_TO_JS[s.dayOfWeek];
            if (js !== undefined) { if (!recurringByDOW[js]) recurringByDOW[js]=[]; recurringByDOW[js].push(s); }
        } else if (s.specificDate) {
            if (!specificByDate[s.specificDate]) specificByDate[s.specificDate]=[];
            specificByDate[s.specificDate].push(s);
        }
    });

    const year           = viewDate.getFullYear();
    const month          = viewDate.getMonth();
    const today          = new Date(); today.setHours(0,0,0,0);
    const daysInMonth    = new Date(year, month+1, 0).getDate();
    const firstDayOfWeek = new Date(year, month, 1).getDay();

    const calendarCells = [
        ...Array(firstDayOfWeek).fill(null),
        ...Array.from({ length: daysInMonth }, (_, i) => i+1),
    ];
    const rem = calendarCells.length % 7;
    if (rem !== 0) calendarCells.push(...Array(7-rem).fill(null));
    const numRows = Math.ceil(calendarCells.length / 7);

    const getDateObj     = (day) => new Date(year, month, day);
    const isDayAvailable = (day) => {
        if (!day) return false;
        const d = getDateObj(day);
        if (d < today) return false;
        const ds = toDateStr(d);
        return !!(specificByDate[ds]?.length > 0 || recurringByDOW[d.getDay()]?.length > 0);
    };
    const isSelected = (day) => !!day && !!selectedDate && day===selectedDate.getDate() && month===selectedDate.getMonth() && year===selectedDate.getFullYear();
    const isToday    = (day) => !!day && getDateObj(day).getTime()===today.getTime();
    const isPast     = (day) => !!day && getDateObj(day)<today;

    const meetingDuration = profile?.meetingDurationMinutes || 30;

    const slotsForSelected = selectedDate ? (() => {
        const ds = toDateStr(selectedDate);
        const src = specificByDate[ds]?.length > 0 ? specificByDate[ds] : (recurringByDOW[selectedDate.getDay()] || []);
        return src.flatMap(s => generateSlots(s.startTime, s.endTime, meetingDuration));
    })() : [];

    const handleDayClick = (day) => {
        if (!isDayAvailable(day)) return;
        setSelectedDate(getDateObj(day)); setSelectedTime(null);
    };

    const prevMonth = () => { const p=subMonths(viewDate,1); if(p>=startOfMonth(today)) setViewDate(p); };
    const nextMonth = () => setViewDate(addMonths(viewDate,1));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedDate||!selectedTime) { setError('Please select a day and time.'); return; }
        if (!requesterName.trim())        { setError('Your name is required.');         return; }
        if (!requesterEmail.trim())       { setError('Your email is required so we can send a confirmation.'); return; }
        setError(''); setSubmitting(true);
        try {
            // Preserve the concrete date+time (and computed end) so the backend can build a calendar invite
            const pad = n => String(n).padStart(2, '0');
            const dateStr = `${selectedDate.getFullYear()}-${pad(selectedDate.getMonth() + 1)}-${pad(selectedDate.getDate())}`;
            const bookingStart = `${dateStr}T${selectedTime}`;
            const [sh, sm] = selectedTime.split(':').map(Number);
            const endTotal = sh * 60 + sm + meetingDuration;
            const bookingEnd = `${dateStr}T${pad(Math.floor(endTotal / 60) % 24)}:${pad(endTotal % 60)}:00`;
            const res = await fetch(`${API_URL}/public/book/${username}`, {
                method:'POST', headers:{ 'Content-Type':'application/json' },
                body: JSON.stringify({ requesterName:requesterName.trim(), requesterEmail:requesterEmail.trim(), message:message.trim()||null, dayOfWeek:JS_TO_DAY[selectedDate.getDay()], preferredTime:selectedTime, bookingStart, bookingEnd }),
            });
            if (!res.ok) throw new Error();
            setSubmitted(true);
        } catch { setError('Something went wrong. Please try again.'); }
        finally { setSubmitting(false); }
    };

    const displayName = profile?.firstName && profile?.lastName
        ? `${profile.firstName} ${profile.lastName}`
        : profile?.username || username;

    // ── States ────────────────────────────────────────────────────────────────
    if (loading) return (
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', bgcolor:'#F8FAFC' }}>
            <CircularProgress sx={{ color:'#7C3AED' }} />
        </Box>
    );

    if (notFound) return (
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', bgcolor:'#F8FAFC' }}>
            <Box sx={{ textAlign:'center' }}>
                <Box sx={{ width:72, height:72, borderRadius:'50%', background:'linear-gradient(135deg,#EDE9FE,#C7D2FE)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:3 }}>
                    <CalendarIcon sx={{ fontSize:32, color:'#7C3AED' }} />
                </Box>
                <Typography variant="h5" fontWeight={800} gutterBottom>Page not found</Typography>
                <Typography color="text.secondary">No booking page found for @{username}.</Typography>
            </Box>
        </Box>
    );

    if (submitted) return (
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', bgcolor:'#F8FAFC' }}>
            <Box sx={{ textAlign:'center', maxWidth:380, px:3 }}>
                <Box sx={{ width:88, height:88, borderRadius:'50%', background:'linear-gradient(135deg,#7C3AED,#4F46E5)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:3, boxShadow:'0 12px 32px rgba(124,58,237,0.3)' }}>
                    <Typography sx={{ fontSize:38 }}>🎉</Typography>
                </Box>
                <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color:'#0F172A' }}>Request sent!</Typography>
                <Typography color="text.secondary" sx={{ fontSize:'1rem', lineHeight:1.7 }}>
                    {displayName} will be in touch to confirm your booking.
                </Typography>
            </Box>
        </Box>
    );

    // ── Main layout ───────────────────────────────────────────────────────────
    return (
        <Box sx={{
            height: { md: '100vh' },
            minHeight: { xs: '100vh', md: 'unset' },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            overflow: { md: 'hidden' },
            bgcolor: '#F8FAFC',
        }}>

            {/* ── LEFT SIDEBAR: Host info ───────────────────────────────────── */}
            <Box sx={{
                width: { xs: '100%', md: 260 },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'white',
                borderRight: { md: '1px solid' },
                borderBottom: { xs: '1px solid', md: 'none' },
                borderColor: 'divider',
                overflowY: { md: 'auto' },
            }}>
                {/* Host header */}
                <Box sx={{ px:3, py:3, background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', gap:1.5 }}>
                    <Avatar sx={{ width:60, height:60, fontSize:26, fontWeight:800, bgcolor:'rgba(255,255,255,0.18)', border:'2px solid rgba(255,255,255,0.3)' }}>
                        {displayName[0]?.toUpperCase() || '?'}
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={800} sx={{ color:'white', lineHeight:1.2 }}>{displayName}</Typography>
                        <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.7)' }}>@{username}</Typography>
                    </Box>
                </Box>

                {/* Sidebar body */}
                <Box sx={{ p:3, display:'flex', flexDirection:'column', gap:2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight:1.6 }}>
                        Pick an available date and time below. No account needed.
                    </Typography>

                    {selectedDate && (
                        <Box sx={{ p:2, borderRadius:2.5, bgcolor:'#F5F3FF', border:'1px solid #EDE9FE' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.5 }}>
                                Selected
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color:'#7C3AED' }}>
                                {format(selectedDate, 'EEEE, MMM d')}
                            </Typography>
                            {selectedTime && (
                                <Typography variant="body2" sx={{ color:'#7C3AED', mt:0.25 }}>
                                    {formatTime(selectedTime)}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Legend */}
                    <Box sx={{ display:'flex', flexDirection:'column', gap:0.75 }}>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                            <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:'#7C3AED', flexShrink:0 }} />
                            <Typography variant="caption" color="text.secondary">Available</Typography>
                        </Box>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                            <Box sx={{ width:22, height:22, borderRadius:'50%', border:'2px solid #7C3AED', bgcolor:'#EDE9FE', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                <Typography variant="caption" fontWeight={700} sx={{ color:'#7C3AED', fontSize:'0.55rem' }}>{format(today,'d')}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">Today</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── CALENDAR ──────────────────────────────────────────────────── */}
            <Box sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'white',
                borderRight: { md: '1px solid' },
                borderColor: 'divider',
            }}>
                {/* Month nav */}
                <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', px:3, py:2, borderBottom:'1px solid', borderColor:'divider', flexShrink:0 }}>
                    <Box onClick={prevMonth} sx={{ width:36, height:36, borderRadius:2, display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid', borderColor:'divider', cursor:(year===today.getFullYear()&&month<=today.getMonth())?'not-allowed':'pointer', opacity:(year===today.getFullYear()&&month<=today.getMonth())?0.3:1, transition:'all 0.15s', '&:hover':{ borderColor:'#7C3AED', bgcolor:'#EDE9FE' } }}>
                        <ChevronLeftIcon fontSize="small" />
                    </Box>
                    <Typography variant="h6" fontWeight={800} sx={{ letterSpacing:-0.5 }}>{format(viewDate,'MMMM yyyy')}</Typography>
                    <Box onClick={nextMonth} sx={{ width:36, height:36, borderRadius:2, display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid', borderColor:'divider', cursor:'pointer', transition:'all 0.15s', '&:hover':{ borderColor:'#7C3AED', bgcolor:'#EDE9FE' } }}>
                        <ChevronRightIcon fontSize="small" />
                    </Box>
                </Box>

                {/* Weekday labels */}
                <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', bgcolor:'#FAFAFA', borderBottom:'1px solid', borderColor:'divider', flexShrink:0 }}>
                    {WEEK_LABELS.map((label, i) => (
                        <Box key={i} sx={{ py:1.25, textAlign:'center' }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color:i===0||i===6?'#9CA3AF':'text.secondary', letterSpacing:0.8, fontSize:'0.72rem', textTransform:'uppercase' }}>{label}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Calendar grid — fills remaining height */}
                <Box sx={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gridTemplateRows: { md: `repeat(${numRows}, 1fr)` },
                    overflow: 'hidden',
                    bgcolor: 'white',
                }}>
                    {calendarCells.map((day, idx) => {
                        const col       = idx % 7;
                        const isWeekend = col===0||col===6;
                        const isLastCol = col===6;
                        const isLastRow = idx>=calendarCells.length-7;
                        const avail     = isDayAvailable(day);
                        const sel       = isSelected(day);
                        const todayDay  = isToday(day);
                        const past      = isPast(day);

                        return (
                            <Box key={idx} onClick={() => handleDayClick(day)} sx={{
                                minHeight: { xs: 80, md: 0 },
                                display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start',
                                pt: { xs:1.5, md:1 }, gap:0.5,
                                cursor: avail ? 'pointer' : 'default',
                                bgcolor: sel ? '#7C3AED' : isWeekend ? '#FAFAFA' : 'white',
                                borderRight: isLastCol ? 'none' : '1px solid',
                                borderBottom: isLastRow ? 'none' : '1px solid',
                                borderColor: 'divider',
                                transition: 'background-color 0.15s',
                                '&:hover': avail && !sel ? { bgcolor:'#EDE9FE' } : {},
                            }}>
                                {day && (
                                    <>
                                        <Box sx={{ width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', bgcolor: todayDay&&!sel ? '#EDE9FE' : 'transparent', border: todayDay&&!sel ? '2px solid #7C3AED' : '2px solid transparent' }}>
                                            <Typography variant="body2" fontWeight={avail||todayDay||sel?700:400}
                                                sx={{ fontSize:'0.85rem', color: sel?'white': past?'#D1D5DB': isWeekend?'#9CA3AF':'text.primary' }}>
                                                {day}
                                            </Typography>
                                        </Box>
                                        {avail && (
                                            <Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor: sel?'rgba(255,255,255,0.9)':'#7C3AED' }} />
                                        )}
                                    </>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* ── RIGHT: Time picker + booking form ─────────────────────────── */}
            <Box sx={{
                width: { xs:'100%', md:320 },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                bgcolor: 'white',
                borderTop: { xs:'1px solid', md:'none' },
                borderColor: 'divider',
            }}>
                {/* No date selected */}
                {!selectedDate && (
                    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', p:4, flex:1 }}>
                        <Box sx={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg,#EDE9FE,#C7D2FE)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:2 }}>
                            <CalendarIcon sx={{ fontSize:26, color:'#7C3AED' }} />
                        </Box>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Pick a date</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth:200, mx:'auto', fontSize:'0.8rem' }}>
                            Select an available day on the calendar to see open time slots.
                        </Typography>
                    </Box>
                )}

                {/* Time picker */}
                {selectedDate && slotsForSelected.length > 0 && (
                    <Box sx={{ flexShrink:0 }}>
                        <Box sx={{ px:3, py:2.5, background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)' }}>
                            <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.65)', textTransform:'uppercase', letterSpacing:0.8, fontSize:'0.65rem' }}>
                                {format(selectedDate,'EEEE')}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color:'white' }}>
                                {format(selectedDate,'MMMM d, yyyy')}
                            </Typography>
                        </Box>
                        <Box sx={{ px:3, py:2.5 }}>
                            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1.5 }}>
                                <TimeIcon sx={{ fontSize:15, color:'#7C3AED' }} />
                                <Typography variant="caption" fontWeight={700} sx={{ color:'#7C3AED', textTransform:'uppercase', letterSpacing:0.8, fontSize:'0.68rem' }}>
                                    Select a time
                                </Typography>
                            </Box>
                            <Box sx={{ display:'flex', flexWrap:'wrap', gap:1 }}>
                                {slotsForSelected.map(t => {
                                    const isSel = selectedTime===t;
                                    return (
                                        <Box key={t} onClick={() => setSelectedTime(t)} sx={{
                                            px:2, py:0.75, borderRadius:2, border:'1.5px solid',
                                            borderColor: isSel ? '#7C3AED' : '#E5E7EB',
                                            bgcolor: isSel ? '#7C3AED' : 'white',
                                            cursor:'pointer', transition:'all 0.15s',
                                            '&:hover':{ borderColor:'#7C3AED', bgcolor: isSel ? '#6D28D9' : '#EDE9FE' },
                                        }}>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: isSel?'white':'text.primary', fontSize:'0.82rem' }}>
                                                {formatTime(t)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* No slots */}
                {selectedDate && slotsForSelected.length===0 && (
                    <Box sx={{ p:3, textAlign:'center', flexShrink:0 }}>
                        <Typography color="text.secondary" variant="body2" sx={{ fontSize:'0.82rem' }}>
                            No available times on {format(selectedDate,'MMM d')}.
                        </Typography>
                    </Box>
                )}

                {/* Booking form */}
                {selectedDate && selectedTime && (
                    <Box sx={{ px:3, pb:3, flex:1 }}>
                        <Box sx={{ pt:0.5, pb:2, borderBottom:'1px solid', borderColor:'divider', mb:2.5 }}>
                            <Typography variant="subtitle2" fontWeight={700}>Your details</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {format(selectedDate,'MMM d')} at {formatTime(selectedTime)}
                            </Typography>
                        </Box>

                        {error && <Alert severity="error" sx={{ mb:2, borderRadius:2 }}>{error}</Alert>}

                        <Box component="form" onSubmit={handleSubmit} sx={{ display:'flex', flexDirection:'column', gap:2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.5, fontSize:'0.65rem' }}>Your name *</Typography>
                                <TextField fullWidth variant="outlined" required size="small"
                                    placeholder="What's your name?"
                                    value={requesterName} onChange={e => setRequesterName(e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize:16, color:'text.disabled' }} /></InputAdornment> }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.5, fontSize:'0.65rem' }}>Email *</Typography>
                                <TextField fullWidth variant="outlined" type="email" size="small" required
                                    placeholder="So they can confirm with you"
                                    value={requesterEmail} onChange={e => setRequesterEmail(e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize:16, color:'text.disabled' }} /></InputAdornment> }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.5, fontSize:'0.65rem' }}>Note</Typography>
                                <TextField fullWidth variant="outlined" multiline rows={2} size="small"
                                    placeholder="Add a note (optional)"
                                    value={message} onChange={e => setMessage(e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><NotesIcon sx={{ fontSize:16, color:'text.disabled', mt:0.5 }} /></InputAdornment> }} />
                            </Box>
                            <Button type="submit" variant="contained" size="large" disabled={submitting}
                                sx={{ fontWeight:700, borderRadius:2.5, background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', boxShadow:'0 4px 12px rgba(124,58,237,0.3)', '&:hover':{ boxShadow:'0 6px 18px rgba(124,58,237,0.4)' } }}>
                                {submitting ? 'Sending…' : 'Send booking request'}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default BookTime;
